import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { useBookmarkSelectionStore } from '../../store';
import { CollectionListItem, CollectionsList } from '../CollectionsList';
import { ErrorModal } from '../ErrorModal';
import { CollectionForm } from '../CollectionForm';
import * as S from './CollectionMasterModal.styled';
import { debounce } from '@/utils/debounce';
import { mergeCollectionsList } from '@/utils/helpers';
import { COLLECTIONS_CHUNK_SIZE } from '@/constants/common';
import { Modal } from '@/components/UI/Modal';
import { useToastStore } from '@/store/useToastStore';
import { Button } from '@/components/UI/Button/Button';
import { CloseIcon } from '@/components/UI/Icons/Icons';
import { useUserFavorites } from '@/features/BookmarkCollections/hooks';
import {
  ADD_TO_COLLECTION,
  GET_COLLECTIONS_LIST,
  REMOVE_FROM_COLLECTION,
  GetCollectionsListQuery,
} from '@/features/BookmarkCollections/api';

interface CollectionMasterModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'add' | 'move';
  bookmarks?: string[];
  syncOnSubmit?: boolean;
  actionTitle?: string;
  showLatestBookmark?: boolean;
}

/**
 * Collection master modal manages adding bookmarks to collections.
 * It can be used in two ways:
 * 1. To add a single bookmark to a collection
 * 2. To add multiple bookmarks to multiple collections
 *
 * If you want to add a single bookmark, you can use the latest bookmark from the user data, no need to provide any bookmarks.
 * If you want to add multiple bookmarks, you must provide an array of bookmark IDs.
 *
 * @param bookmarks - Provide only if you want multiple bookmarks to be added to the collection
 * @param showLatestBookmark - By default latest bookmark is being added to collection, this param controls if you want to show latest bookmark in the modal
 */
export const CollectionMasterModal = ({
  actionTitle = '',
  isOpen,
  onClose,
  mode = 'add',
  bookmarks = [],
  syncOnSubmit = false,
  showLatestBookmark = true,
}: CollectionMasterModalProps) => {
  const router = useRouter();
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);

  const openToast = useToastStore(state => state.openToast);
  const closeToast = useToastStore(state => state.closeToast);
  const isSelectionActive = useBookmarkSelectionStore(
    state => state.isSelectionActive
  );
  const { latestBookmark } = useUserFavorites();
  const [addToCollection, { loading: isAdding }] =
    useMutation(ADD_TO_COLLECTION);
  const [removeFromCollection] = useMutation(REMOVE_FROM_COLLECTION);

  const { data, loading, refetch, fetchMore } =
    useQuery<GetCollectionsListQuery>(GET_COLLECTIONS_LIST, {
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'network-only',
      variables: {
        first: COLLECTIONS_CHUNK_SIZE,
      },
      errorPolicy: 'all',
    });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [isErrorOpen, setIsErrorOpen] = useState(false);

  const common = data?.currentUser?.bookmarkCollections;
  const collections = common?.nodes ?? [];
  const { endCursor, hasNextPage } = common?.pageInfo ?? {};
  const isCreateFormOpen = isFormOpen || collections.length < 1;
  const collection = collections.find(c => c.id === selected[0]);
  const successName = collection?.name;

  const getSuccessText = () => {
    if (bookmarks.length === 0 && latestBookmark && selected.length > 1) {
      return t(`bookmark_collections.${mode}_to_collection_one_to_many`);
    }

    if (bookmarks.length === 0 && latestBookmark && selected.length === 1) {
      return t(`bookmark_collections.${mode}_to_collection_one`, {
        name: successName,
      });
    }

    if (selected.length === 1 && bookmarks.length === 1) {
      return t(`bookmark_collections.${mode}_to_collection_one`, {
        name: successName,
      });
    }

    if (bookmarks.length > 1 && selected.length === 1) {
      return t(`bookmark_collections.${mode}_to_collection_many_to_one`, {
        name: successName,
      });
    }

    if (bookmarks.length > 1 && selected.length > 1) {
      return t(`bookmark_collections.${mode}_to_collection_many_to_many`);
    }

    if (bookmarks.length === 1 && selected.length > 1) {
      return t(`bookmark_collections.${mode}_to_collection_one_to_many`);
    }

    return '';
  };

  useEffect(() => {
    const listEl = listRef.current;

    if (!listEl) return;

    const handleScroll = debounce(() => {
      const scrollOffset = listEl.scrollHeight - listEl.scrollTop;
      const threshold = listEl.clientHeight + 50;
      const nearBottom = scrollOffset <= threshold;

      if (nearBottom && !loading && hasNextPage) {
        fetchMore({
          variables: { after: endCursor },
          updateQuery: (prev, { fetchMoreResult }) =>
            mergeCollectionsList(prev, fetchMoreResult),
        });
      }
    }, 100);

    listEl.addEventListener('scroll', handleScroll);
    return () => listEl.removeEventListener('scroll', handleScroll);
  }, [fetchMore, loading, hasNextPage, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelected([]);
      setIsFormOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isSelectionActive) {
      closeToast();
    }
  }, [isSelectionActive]);

  const createCollectionSuccess = async () => {
    await refetch();
    setIsFormOpen(false);
  };

  const handleAddToCollection = useCallback((cId: string) => {
    setSelected(p =>
      p.includes(cId) ? p.filter(cid => cid !== cId) : [...p, cId]
    );
  }, []);

  const handleActionClick = () => {
    if (selected.length > 1) {
      router.push('/collections');
    } else {
      router.push(`/collections/${collection?.id}`);
    }
  };

  const handleCloseCreateForm = () => {
    if (isFormOpen) {
      setIsFormOpen(false);
      return;
    }
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const adLegacyIdsFromLatest =
        showLatestBookmark && latestBookmark?.legacyResourceId != null
          ? [String(latestBookmark.legacyResourceId)]
          : [];
      const adLegacyIds =
        bookmarks.length > 0 ? bookmarks : adLegacyIdsFromLatest;

      if (mode === 'move') {
        await removeFromCollection({
          variables: {
            adLegacyIds: bookmarks,
            bookmarkCollectionIds: [router.query.id],
          },
        });
      }

      if (!adLegacyIds.length) {
        onClose();
        return;
      }

      await addToCollection({
        variables: {
          bookmarkCollectionIds: selected,
          adLegacyIds,
        },
      });

      if (syncOnSubmit) refetch();

      onClose();
      openToast({
        text: getSuccessText(),
        action: t('bookmark_collections.toast_success_action'),
        onActionClick: handleActionClick,
      });
    } catch (_) {
      onClose();
      setIsErrorOpen(true);
    }
  };

  return (
    <>
      <Modal isScrollLocked isOpen={isOpen} onClose={onClose} maxWidth={600}>
        <S.Content>
          {isCreateFormOpen && !loading ? (
            <>
              <S.Header $isCentered>
                <S.Title>{t('bookmark_collections.form_title')}</S.Title>
                <S.CloseButton onClick={handleCloseCreateForm}>
                  <CloseIcon name="close" width={20} height={20} />
                </S.CloseButton>
              </S.Header>
              <CollectionForm onSuccess={createCollectionSuccess} />
            </>
          ) : (
            <>
              <S.Header>
                <S.Title>
                  {t('bookmark_collections.collection_master_modal_title')}
                </S.Title>

                <S.CloseButton onClick={onClose}>
                  <CloseIcon width={20} height={20} />
                </S.CloseButton>
              </S.Header>

              <S.StickySection>
                {showLatestBookmark && latestBookmark && (
                  <CollectionListItem
                    isSelectable={false}
                    image={latestBookmark?.photo?.url}
                    title={t('bookmark_collections.is_bookmarked')}
                  />
                )}
                <S.Section>
                  <S.SectionTitle>
                    {t('bookmark_collections.title')}
                  </S.SectionTitle>
                  <S.CreateLink onClick={() => setIsFormOpen(true)}>
                    {t('bookmark_collections.drawer_create_link')}
                  </S.CreateLink>
                </S.Section>
              </S.StickySection>

              <CollectionsList
                ref={listRef}
                selected={selected}
                collections={collections}
                handleAddToCollection={handleAddToCollection}
                loading={loading && !!collections.length}
              />

              <S.Footer>
                <Button
                  isFullWidth
                  borderRadius={14}
                  appearance="primary"
                  onClick={handleSubmit}
                  isLoading={isAdding}
                  disabled={!selected.length || isAdding}
                >
                  {actionTitle || t('bookmark_collections.drawer_add_button')}
                </Button>
              </S.Footer>
            </>
          )}
        </S.Content>
      </Modal>

      <ErrorModal
        isOpen={isErrorOpen}
        onClose={() => setIsErrorOpen(false)}
        message={t('bookmark_collections.universal_error_message')}
      />
    </>
  );
};
